import {
  ALL_GAME_IDS,
  MISSION_TYPES,
  createDefaultGameProgress,
} from "../constants/missions";

export const getLocalDayKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const dayOffset = (dayKey, days) => {
  const [y, m, d] = dayKey.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  return getLocalDayKey(date);
};

export const applyGameEvent = (progressInput, type, payload = {}) => {
  const progress = {
    ...createDefaultGameProgress(),
    ...(progressInput || {}),
    gamesPlayed: {
      ...createDefaultGameProgress().gamesPlayed,
      ...(progressInput?.gamesPlayed || {}),
    },
    uniqueGamesPlayed: [...(progressInput?.uniqueGamesPlayed || [])],
    playDates: [...(progressInput?.playDates || [])],
  };

  if (type === "game_played") {
    const gameId = payload.gameId;
    if (gameId && progress.gamesPlayed[gameId] !== undefined) {
      progress.gamesPlayed[gameId] = (progress.gamesPlayed[gameId] || 0) + 1;
      if (!progress.uniqueGamesPlayed.includes(gameId)) {
        progress.uniqueGamesPlayed.push(gameId);
      }
    }

    const today = getLocalDayKey();
    if (progress.lastPlayDate !== today) {
      if (progress.lastPlayDate === dayOffset(today, -1)) {
        progress.dayStreak = (progress.dayStreak || 0) + 1;
      } else {
        progress.dayStreak = 1;
      }
      progress.lastPlayDate = today;
      if (!progress.playDates.includes(today)) {
        progress.playDates = [...progress.playDates, today].slice(-60);
      }
    }
  }

  if (type === "hangman_word_won") {
    progress.hangmanWinStreak = (progress.hangmanWinStreak || 0) + 1;
    progress.hangmanBestWinStreak = Math.max(
      progress.hangmanBestWinStreak || 0,
      progress.hangmanWinStreak
    );
  }

  if (type === "hangman_word_lost") {
    progress.hangmanWinStreak = 0;
  }

  return progress;
};

export const getMissionProgressView = (mission, gameProgress, missionState) => {
  const type = mission?.type || MISSION_TYPES.MANUAL_CONTENT;
  const target =
    Number(mission?.config?.target) ||
    (type === MISSION_TYPES.PLAY_ALL_GAMES ? ALL_GAME_IDS.length : 1);
  const gp = gameProgress || createDefaultGameProgress();
  const state = missionState || {};

  if (state.status === "claimed" || state.status === "completed") {
    return {
      progress: target,
      target,
      status: state.status === "claimed" ? "claimed" : "completed",
      complete: true,
    };
  }

  if (type === MISSION_TYPES.PLAY_ALL_GAMES) {
    const progress = ALL_GAME_IDS.filter((id) =>
      (gp.uniqueGamesPlayed || []).includes(id)
    ).length;
    return {
      progress,
      target: ALL_GAME_IDS.length,
      status: progress >= ALL_GAME_IDS.length ? "completed" : "in_progress",
      complete: progress >= ALL_GAME_IDS.length,
    };
  }

  if (type === MISSION_TYPES.DAILY_PLAY_STREAK) {
    const progress = gp.dayStreak || 0;
    return {
      progress: Math.min(progress, target),
      target,
      status: progress >= target ? "completed" : "in_progress",
      complete: progress >= target,
    };
  }

  if (type === MISSION_TYPES.HANGMAN_WIN_STREAK) {
    const progress = gp.hangmanWinStreak || 0;
    return {
      progress: Math.min(progress, target),
      target,
      status: progress >= target ? "completed" : "in_progress",
      complete: progress >= target,
    };
  }

  return {
    progress: 0,
    target: 1,
    status: "in_progress",
    complete: false,
  };
};

export const findCompletableMissions = (
  missions,
  gameProgress,
  missionProgressMap
) => {
  const completed = [];
  (missions || []).forEach((mission) => {
    if (!mission?.active && mission?.active !== undefined && mission.active === false) {
      return;
    }
    if (mission.type === MISSION_TYPES.MANUAL_CONTENT) return;
    const reward = Number(mission.rewardPoints) || 0;
    if (reward <= 0) return;

    const existing = missionProgressMap?.[mission.id];
    if (existing?.status === "claimed") return;

    const view = getMissionProgressView(mission, gameProgress, existing);
    if (view.complete) {
      completed.push({ mission, view });
    }
  });
  return completed;
};
